import React, { useMemo } from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { ValuePropsBarBlock } from "../../../common/ValuePropsBarBlock";
import { IProductID } from "../../../models/nominals";
import { IValuePropBarBlock } from "../../../models/value-props";
import { useAppSelector } from "../../reducers";
import { rootProductSelector } from "../selectors";

interface IProps {
    blockProductID: IProductID;
    content: string;
}

const extractValuePropsData = (content: string): IValuePropBarBlock | null => {
    // Create a temporary DOM element to parse the HTML content
    const document = getDocument();
    if (!document) {
        return null;
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const propsElem = tempDiv.querySelector("script[type='application/json']");
    if (!propsElem) {
        return null;
    }
    try {
        // Parse the JSON data - DOM APIs handle HTML entity decoding automatically
        const dataProps = JSON.parse(propsElem.textContent);
        return JSON.parse(dataProps.cms);
    } catch (err) {
        console.warn("Failed to parse value props from content", err);
        return null;
    }
};

export const PDPSelectedRootProductConditionalBlock = (
    props: IProps,
): React.ReactNode => {
    const selectedRootProduct = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );

    // If the currently selected value of the configured attribute doesn't match the desired value, don't render anything
    const isSelected = props.blockProductID === selectedRootProduct?.id;
    // Build IDs for relationship to tab controls. These IDs must match the IDs used by PDPChangeRootProductBlock.
    const tabControlID = `change-root-product-block-${props.blockProductID}`;
    const tabPanelID = `root-product-conditional-block-${props.blockProductID}`;

    // Extract and parse the Value Props data from the innerHTML content string.
    // This avoids relying on static dangerouslySetInnerHTML rendering for the value props,
    // allowing React to build and manage the ValuePropsBarBlock component properly.
    // This change fixes issues with event listeners not working due to server-side pre-assembly of HTML.
    // PERF: DOM parsing and JSON extraction is expensive - cache result across re-renders
    const valuePropsData = useMemo(
        () => extractValuePropsData(props.content),
        [props.content],
    );

    return (
        <div
            id={tabPanelID}
            role="tabpanel"
            aria-labelledby={tabControlID}
            aria-hidden={!isSelected}
            style={{ display: isSelected ? "block" : "none" }}
            {...(!valuePropsData && {
                dangerouslySetInnerHTML: { __html: props.content },
            })}
        >
            {valuePropsData && (
                <ValuePropsBarBlock valuePropsData={valuePropsData} />
            )}
        </div>
    );
};
